import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { lazy, memo, Suspense, useMemo, useRef, useState } from 'react';

import type { RelevancyCardProps } from '../../DynamicCardComponent';
import { useRelevancyOptions } from '../../store';
import BaseCardComponent from '../BaseCardComponent';

import { useWindowSize } from '@hooks/useWindowSize';
import { getPhotogalleryByIdQuery } from '@queries/editorial';
import { trackCardElement } from '@services/GoogleTagManagerService';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import GoogleTagManagerHelper from '@tracking/gtm-helper';

const FullScreen = lazy(() => import('./FullScreen'));
const MobileCard = lazy(() => import('./MobileCard'));

const PhotogalleryComponent = (props: RelevancyCardProps) => {
  const {
    item: { teaser },
  } = props;
  const { options } = useRelevancyOptions();
  const { isMobile } = useWindowSize();
  const { data: photogallery } = useQuery({ ...getPhotogalleryByIdQuery(teaser.id), select: undefined });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [open, setOpen] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);
  const setRef = (node: HTMLDivElement) => {
    if (!node) return;
    cardRef.current = node;
    trackCardElement(trackingData, true)(node);
  };

  const sponsorName = useMemo(
    () => !options.disableSponsors && (photogallery.sponsor?.type ?? photogallery.sponsors?.at(0)),
    [options.disableSponsors, photogallery.sponsor, photogallery.sponsors],
  );
  const { sponsor } = useSponsor(sponsorName);

  const isSponsoredCard = useMemo(() => !options.disableSponsors && !!sponsor, [options.disableSponsors, sponsor]);

  const firstPhoto = photogallery.photos?.at(0);

  const trackingVariant = useMemo(() => {
    if (!isMobile) return 'desktop';

    return photogallery?.photos?.length > 4 ? '4+ images' : `${photogallery?.photos?.length ?? 0} images`;
  }, [isMobile, photogallery?.photos?.length]);

  const trackingData = useMemo(() => {
    if (!firstPhoto) return null;
    return {
      id: [firstPhoto.id, photogallery?.photos?.length].join('-'),
      name: [isSponsoredCard && sponsor?.code, 'Photo gallery'].filter(Boolean).join(' '),
      category: 'photo gallery card',
      variant: trackingVariant,
      item_category3: sponsor?.code ? 'sponsored' : undefined,
      item_category4: sponsor?.code,
    };
  }, [firstPhoto, isSponsoredCard, photogallery?.photos?.length, sponsor?.code, trackingVariant]);

  const handleClick =
    (index = 0) =>
    () => {
      setOpen(true);
      setCurrentSlide(index);

      GoogleTagManagerHelper.gtmCardElementClick(cardRef.current, trackingData);
    };

  const handleClose = () => {
    setOpen(false);

    GoogleTagManagerHelper.event(
      'Click',
      'Close image',
      'photo gallery card',
      { section_reference: 'photo_detail', interaction: 'Close image' },
      'photo_detail',
    );
  };

  return (
    <>
      <div className={clsx('card', photogallery?.photos?.length && 'card--gallery')} ref={setRef}>
        <Suspense>
          {isMobile ? (
            <MobileCard sponsorName={sponsorName} photogallery={photogallery} handleClick={handleClick} />
          ) : (
            <BaseCardComponent
              {...props}
              sponsorCode={sponsorName}
              title={photogallery.title}
              image={firstPhoto?.image}
              onClick={handleClick()}
            />
          )}
        </Suspense>
      </div>
      <Suspense>
        <FullScreen
          showModal={open}
          photos={photogallery.photos}
          currentSlide={currentSlide}
          title={photogallery.title}
          sponsorName={sponsorName}
          closeModal={handleClose}
        />
      </Suspense>
    </>
  );
};

export default memo(PhotogalleryComponent);
